import React, { forwardRef, memo, useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";

import { LoadingView, ErrorView } from "components/Feedback";

import { enhanceColDefsForSort } from "./sortHelpers";
import "./styles.css";

const ROW_STYLE = { cursor: "pointer" };
const NoRowsOverlay = () => "Пусто.";

/**
 * Единая таблица (ag-grid).
 * - rowKey: имя поля-идентификатора строки (docid, itemid...)
 * - selectedId + onSelect: выделение строки одним кликом
 * - onOpen: открытие записи ДВОЙНЫМ кликом
 * - loading / error / onRetry: единые состояния
 * - bottomRow: закреплённая итоговая строка
 */
export const Table = memo(
  forwardRef(function Table(
    {
      rowData,
      colDefs,
      height = 500,
      fill,
      rowKey,
      selectedId,
      onSelect,
      onOpen,
      loading,
      error,
      onRetry,
      bottomRow,
      ...rest
    },
    ref
  ) {
    // Стабильные id строк: ag-grid сохраняет позицию скролла
    // при догрузке следующих страниц (infinite scroll)
    const getRowId = useCallback(
      (params) => {
        const value = params.data?.[rowKey];
        return value !== undefined && value !== null
          ? String(value)
          : `pinned-${params.level ?? 0}`;
      },
      [rowKey]
    );

    const handleRowClicked = useCallback(
      (event) => {
        if (event.node.rowPinned) return;
        if (rowKey && event.data?.[rowKey] !== undefined) {
          onSelect?.(event.data);
        }
      },
      [rowKey, onSelect]
    );

    const handleRowDoubleClicked = useCallback(
      (event) => {
        if (event.node.rowPinned) return;
        if (rowKey && event.data?.[rowKey] !== undefined) {
          onOpen?.(event.data);
        }
      },
      [rowKey, onOpen]
    );

    const getRowStyle = useCallback(
      (params) => {
        if (params.node.rowPinned) {
          return {
            fontWeight: "600",
            backgroundColor: "var(--mainBackgroundColor)",
          };
        }
        const isSelected =
          rowKey &&
          selectedId !== undefined &&
          selectedId !== null &&
          String(params.data?.[rowKey]) === String(selectedId);

        return isSelected
          ? {
              backgroundColor: "var(--secondaryColor)",
              fontWeight: "500",
              color: "var(--primaryColor)",
            }
          : undefined;
      },
      [rowKey, selectedId]
    );

    const defaultColDef = useMemo(
      () => ({ resizable: true, suppressMovable: true }),
      []
    );

    const columnDefs = useMemo(
      () => enhanceColDefsForSort(colDefs),
      [colDefs]
    );

    const loadingHeight = fill ? undefined : height;
    if (loading) return <LoadingView height={loadingHeight} />;
    if (error)
      return (
        <ErrorView message={error} onRetry={onRetry} height={loadingHeight} />
      );

    return (
      <div
        className={`ag-theme-quartz fp-table${fill ? " fp-table-fill" : ""}`}
        style={fill ? undefined : { height }}
      >
        <AgGridReact
          ref={ref}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          {...(rowKey ? { getRowId } : {})}
          rowSelection="single"
          onRowClicked={handleRowClicked}
          onRowDoubleClicked={handleRowDoubleClicked}
          rowStyle={ROW_STYLE}
          getRowStyle={getRowStyle}
          animateRows={false}
          pinnedBottomRowData={
            bottomRow && rowData?.length ? [bottomRow] : undefined
          }
          noRowsOverlayComponent={NoRowsOverlay}
          {...rest}
        />
      </div>
    );
  })
);
